import { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';

import { IconEye as Visibility } from '@/components/icon/tabler/IconEye';
import { IconEyeOff as VisibilityOff } from '@/components/icon/tabler/IconEyeOff';
import { IconAlertCircle as ErrorIcon } from '@/components/icon/tabler/IconAlertCircle';
import Button from '@/components/Button';
import AuroraBackdrop from '@/components/AuroraBackdrop';
import AuthCardContainer from '@/domain/auth/components/AuthCardContainer';
import { useResetPassword } from '@/api/hooks';
import { ScreenRoutes } from '@/types/router';
import { resetPasswordSchema } from '@/domain/auth/schemas';
import { type ResetPasswordRequest } from '@/api/routes/public/auth.api';
import { getUserMessage } from '@/utils/error';
import { useTranslate } from '@/state/i18n';

type FormData = ResetPasswordRequest & { confirmPassword: string };

const ResetPasswordContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100dvh',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
  },
}));

const ResetPassword = () => {
  const t = useTranslate('domain.auth.ResetPassword');
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    resolver: joiResolver(resetPasswordSchema),
    mode: 'onTouched',
  });

  const { mutate: resetPassword, isPending, error } = useResetPassword();

  useEffect(() => {
    if (token) {
      setValue('token', token);
    }
  }, [token, setValue]);

  const onSubmit = useCallback(
    (data: FormData) => {
      resetPassword(
        { token: data.token, newPassword: data.newPassword },
        {
          onSuccess: () => {
            enqueueSnackbar(t('done'), { variant: 'success' });
            navigate(ScreenRoutes.SIGNIN);
          },
        },
      );
    },
    [resetPassword, navigate, enqueueSnackbar, t],
  );

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  if (!token) {
    return (
      <ResetPasswordContainer direction="column" justifyContent="space-between">
        <AuroraBackdrop />
        <AuthCardContainer>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Stack direction="column" gap={1} alignItems="center">
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />
              <Typography variant="h4" component="h1" sx={{ textAlign: 'center' }}>
                {t('invalidLink')}
              </Typography>
            </Stack>
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              {t('invalidLinkBody')}
            </Typography>
            <Button
              sx={{ mt: 3 }}
              component={RouterLink}
              onClick={() => navigate(ScreenRoutes.SIGNIN)}
              variant="contained"
              fullWidth
            >
              {t('backToSignIn')}
            </Button>
          </Box>
        </AuthCardContainer>
      </ResetPasswordContainer>
    );
  }

  return (
    <ResetPasswordContainer direction="column" justifyContent="space-between">
      <AuroraBackdrop />
      <AuthCardContainer redline={t('title')}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {email && (
            <Typography sx={{ mb: 2 }} variant="body2">
              {t('resettingFor')}
              <strong>{email}</strong>
            </Typography>
          )}

          <FormControl fullWidth>
            <FormLabel>{t('newPassword')}</FormLabel>
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type={showPassword ? 'text' : 'password'}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  placeholder="••••••"
                  required
                  autoComplete="new-password"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <FormLabel>{t('confirmPassword')}</FormLabel>
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type={showConfirmPassword ? 'text' : 'password'}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  placeholder="••••••"
                  required
                  autoComplete="new-password"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={handleClickShowConfirmPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </FormControl>

          {error && (
            <Alert severity="error">
              <AlertTitle>{getUserMessage(error, t('error'))}</AlertTitle>
            </Alert>
          )}

          <Button sx={{ mt: 2 }} type="submit" fullWidth variant="contained" loading={isPending}>
            {t('send')}
          </Button>

          <Typography sx={{ textAlign: 'center' }}>
            {t('rememberedPassword')}
            <Link component={RouterLink} to={ScreenRoutes.SIGNIN} variant="body2">
              {t('signIn')}
            </Link>
          </Typography>
        </Box>
      </AuthCardContainer>
    </ResetPasswordContainer>
  );
};

export default memo(ResetPassword);
